import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  backdropUrl,
  bestFormat,
  createId,
  displayTitle,
  effectiveLocationId,
  formatDate,
  formatDuration,
  formatRelativeDate,
  genreNames,
  locationName,
  type Copy,
  type Movie,
  type Watch,
} from '@physical-movies/core';
import { useCollection, useI18n, useTmdb, useToast } from '../app/context';
import { CopySheet } from '../components/CopySheet';
import { Icon } from '../components/Icon';
import { Section } from '../components/Page';
import { PosterImage } from '../components/Poster';
import { Sheet } from '../components/Sheet';
import { WatchSheet, type WatchDraft } from '../components/WatchSheet';
import { Button, EmptyState, IconButton, StarsReadonly } from '../components/ui';
import { useFormatLabels } from '../lib/labels';
import { useScrollToTop } from '../lib/useScrollRestoration';
import styles from './movie.module.css';

export function MovieDetail() {
  useScrollToTop();
  const { movieId } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const toast = useToast();
  const tmdb = useTmdb();
  const collection = useCollection();
  const formatLabels = useFormatLabels();

  const movie = movieId ? collection.movieById.get(movieId) : undefined;
  const watches = movieId ? (collection.watchesByMovie.get(movieId) ?? []) : [];

  // `seq` keys the sheets: bumping it on open remounts the form so it seeds
  // itself from the entry being edited, and a new watch gets a fresh "now".
  const [watchSheet, setWatchSheet] = useState<{
    open: boolean;
    editing: Watch | null;
    seq: number;
  }>({ open: false, editing: null, seq: 0 });
  const [copySheet, setCopySheet] = useState<{
    open: boolean;
    editing: Copy | null;
    seq: number;
  }>({ open: false, editing: null, seq: 0 });

  const openWatchSheet = (editing: Watch | null) =>
    setWatchSheet((current) => ({ open: true, editing, seq: current.seq + 1 }));
  const closeWatchSheet = () => setWatchSheet((current) => ({ ...current, open: false }));
  const openCopySheet = (editing: Copy | null) =>
    setCopySheet((current) => ({ open: true, editing, seq: current.seq + 1 }));
  const closeCopySheet = () => setCopySheet((current) => ({ ...current, open: false }));
  const [moreOpen, setMoreOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  if (!movie) {
    return (
      <EmptyState
        icon="search"
        title={t('movie.notFound.title')}
        body={t('movie.notFound.body')}
        action={<Button onClick={() => void navigate('/')}>{t('movie.backToCollection')}</Button>}
      />
    );
  }

  const title = displayTitle(movie, locale);
  const overview = locale === 'de' ? (movie.overviewDe ?? movie.overviewEn) : movie.overviewEn;
  const format = bestFormat(movie);
  const genres = genreNames(movie.genreIds, locale);
  const backdrop = backdropUrl(movie.backdropPath, 'w780');

  const meta = [
    movie.year ?? null,
    movie.runtime ? formatDuration(movie.runtime, t) : t('movie.runtimeUnknown'),
  ].filter(Boolean);

  const logNow = async () => {
    await collection.logWatch(movie, { watchedAt: new Date().toISOString() });
    toast.show(t('watch.logged'));
  };

  const saveWatch = async (draft: WatchDraft) => {
    if (watchSheet.editing) {
      await collection.updateWatch(watchSheet.editing, draft);
    } else {
      await collection.logWatch(movie, draft);
      toast.show(t('watch.logged'));
    }
    closeWatchSheet();
  };

  const removeWatch = async () => {
    if (!watchSheet.editing) return;
    await collection.deleteWatch(watchSheet.editing);
    closeWatchSheet();
    toast.show(t('watch.deleted'));
  };

  const saveCopy = async (copy: Copy) => {
    const editing = copySheet.editing;
    const copies = editing
      ? movie.copies.map((entry) => (entry.id === editing.id ? copy : entry))
      : [...movie.copies, { ...copy, id: copy.id || createId() }];
    await collection.setCopies(movie, copies);
    closeCopySheet();
    toast.show(editing ? t('copy.updated') : t('copy.added'));
  };

  const removeCopy = async () => {
    if (!copySheet.editing) return;
    const editingId = copySheet.editing.id;
    await collection.setCopies(
      movie,
      movie.copies.filter((entry) => entry.id !== editingId),
    );
    closeCopySheet();
    toast.show(t('copy.removed'));
  };

  const refreshFromTmdb = async () => {
    if (!movie.tmdbId) return;
    setRefreshing(true);
    try {
      const details = await tmdb.getMovie(movie.tmdbId);
      await collection.updateMovie(movie.id, {
        title: details.title,
        titleDe: details.titleDe,
        originalTitle: details.originalTitle,
        year: details.year,
        runtime: details.runtime,
        posterPath: details.posterPath,
        backdropPath: details.backdropPath,
        overviewEn: details.overviewEn,
        overviewDe: details.overviewDe,
        genreIds: details.genreIds,
        directors: details.directors,
      });
      toast.show(t('movie.refreshed'));
      setMoreOpen(false);
    } catch {
      toast.show(t('add.loadFailed'), 'danger');
    } finally {
      setRefreshing(false);
    }
  };

  const removeMovie = async () => {
    const confirmed = window.confirm(
      `${t('movie.deleteConfirm.title', { title })}\n\n${t('movie.deleteConfirm.body', {
        count: watches.length,
      })}`,
    );
    if (!confirmed) return;
    await collection.deleteMovie(movie);
    toast.show(t('movie.deleted', { title }));
    void navigate('/');
  };

  const saveNote = (value: string) => {
    const next = value.trim() ? value.trim() : null;
    if (next === (movie.personalNote ?? null)) return;
    void collection.updateMovie(movie.id, { personalNote: next });
  };

  return (
    <div className={styles.page}>
      <div className={styles.topRow}>
        <Link to="/" className={styles.back}>
          <Icon name="chevronLeft" size={16} />
          {t('nav.collection')}
        </Link>
        <IconButton name="more" label={t('common.more')} onClick={() => setMoreOpen(true)} />
      </div>

      <div className={styles.hero}>
        {backdrop && (
          <div className={styles.backdrop} aria-hidden="true">
            <img src={backdrop} alt="" loading="eager" decoding="async" />
          </div>
        )}
        <div className={styles.heroInner}>
          <div className={styles.poster}>
            {format && <span className={styles.spine} data-format={format} aria-hidden="true" />}
            <PosterImage posterPath={movie.posterPath} title={title} size="w342" eager />
          </div>
          <div className={styles.heroText}>
            <h1 className={styles.title}>{title}</h1>
            {movie.originalTitle && movie.originalTitle !== title && (
              <span className={styles.originalTitle}>{movie.originalTitle}</span>
            )}
            <div className={styles.metaRow}>{meta.join(' · ')}</div>
            {movie.directors.length > 0 && (
              <span className={styles.directors}>
                {t('movie.directedBy', { names: movie.directors.join(', ') })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <Button variant="primary" size="lg" onClick={() => void logNow()}>
          <Icon name="check" size={18} />
          {t('movie.logWatch')}
        </Button>
        <Button
          size="lg"
          aria-label={t('movie.logWatchDetails')}
          title={t('movie.logWatchDetails')}
          onClick={() => openWatchSheet(null)}
        >
          <Icon name="pencil" size={18} />
        </Button>
      </div>

      <p className={styles.playline}>
        {movie.watchCount === 0
          ? t('movie.neverWatched')
          : `${t('movie.watchedTimes', { count: movie.watchCount })}${
              movie.lastWatchedAt
                ? ` · ${t('movie.lastWatched', {
                    date: formatRelativeDate(movie.lastWatchedAt, locale),
                  })}`
                : ''
            }`}
      </p>

      {!movie.runtime && (
        <p className={styles.warning}>
          <Icon name="clock" size={16} />
          {t('movie.addRuntime')}
        </p>
      )}

      <Section
        title={t('movie.copies')}
        aside={movie.copies.length > 0 ? String(movie.copies.length) : undefined}
      >
        <div className={styles.copies}>
          {movie.copies.map((copy) => (
            <button
              key={copy.id}
              type="button"
              className={styles.copyRow}
              onClick={() => openCopySheet(copy)}
            >
              <span className={styles.copySpine} data-format={copy.format} aria-hidden="true" />
              <span className={styles.copyText}>
                <span className={styles.copyName}>{formatLabels.long(copy.format)}</span>
                {(() => {
                  const where = locationName(
                    effectiveLocationId(copy, collection.settings),
                    collection.settings,
                  );
                  const meta = [
                    where,
                    copy.edition,
                    copy.purchasedAt ? formatDate(copy.purchasedAt, locale) : null,
                  ].filter(Boolean);
                  return meta.length > 0 ? (
                    <span className={styles.copyMeta}>{meta.join(' · ')}</span>
                  ) : null;
                })()}
                {copy.loan && (
                  <span className={styles.copyLoan}>
                    <Icon name="handoff" size={13} />
                    {t('copy.lentTo', { name: copy.loan.to })}{' '}
                    {t('copy.lentSince', { date: formatDate(copy.loan.since, locale) })}
                  </span>
                )}
              </span>
              <Icon name="chevronRight" size={16} className={styles.copyChevron} />
            </button>
          ))}
        </div>
        <Button block onClick={() => openCopySheet(null)}>
          <Icon name="plus" size={16} />
          {t('movie.addCopy')}
        </Button>
      </Section>

      {overview && (
        <Section title={t('movie.overview')}>
          <p className={styles.prose}>{overview}</p>
        </Section>
      )}

      {genres.length > 0 && (
        <Section title={t('filter.genres')}>
          <div className={styles.genres}>
            {genres.map((genre) => (
              <span key={genre} className={styles.genre}>
                {genre}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section title={t('movie.note')}>
        <textarea
          className={styles.noteArea}
          defaultValue={movie.personalNote ?? ''}
          maxLength={1000}
          placeholder={t('movie.notePlaceholder')}
          aria-label={t('movie.note')}
          onBlur={(event) => saveNote(event.target.value)}
        />
      </Section>

      <Section
        title={t('movie.watchHistory')}
        aside={watches.length > 0 ? String(watches.length) : undefined}
      >
        {watches.length === 0 ? (
          <p className={styles.empty}>{t('watch.empty')}</p>
        ) : (
          <div className={styles.history}>
            {watches.map((watch) => (
              <button
                key={watch.id}
                type="button"
                className={styles.historyRow}
                onClick={() => openWatchSheet(watch)}
              >
                <span className={styles.historyDate}>{formatDate(watch.watchedAt, locale)}</span>
                <span className={styles.historyNote}>{watch.note ?? ''}</span>
                {watch.rating ? (
                  <span className={styles.historyStars}>
                    <StarsReadonly
                      value={watch.rating}
                      label={t('watch.ratingValue', { count: watch.rating })}
                    />
                  </span>
                ) : (
                  <span />
                )}
              </button>
            ))}
          </div>
        )}
      </Section>

      <WatchSheet
        key={`watch-${watchSheet.seq}`}
        open={watchSheet.open}
        onClose={closeWatchSheet}
        initial={
          watchSheet.editing
            ? {
                watchedAt: watchSheet.editing.watchedAt,
                rating: watchSheet.editing.rating ?? null,
                note: watchSheet.editing.note ?? null,
              }
            : { watchedAt: new Date().toISOString(), rating: null, note: null }
        }
        onSubmit={saveWatch}
        onDelete={watchSheet.editing ? removeWatch : undefined}
      />

      <CopySheet
        key={`copy-${copySheet.seq}`}
        open={copySheet.open}
        onClose={closeCopySheet}
        initial={copySheet.editing}
        onSubmit={saveCopy}
        onDelete={copySheet.editing && movie.copies.length > 1 ? removeCopy : undefined}
      />

      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        movie={movie}
        refreshing={refreshing}
        onRefresh={refreshFromTmdb}
        onDelete={removeMovie}
      />
    </div>
  );
}

function MoreSheet({
  open,
  onClose,
  movie,
  refreshing,
  onRefresh,
  onDelete,
}: {
  open: boolean;
  onClose(): void;
  movie: Movie;
  refreshing: boolean;
  onRefresh(): void | Promise<void>;
  onDelete(): void | Promise<void>;
}) {
  const { t, locale } = useI18n();
  const tmdb = useTmdb();

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={displayTitle(movie, locale)}
      closeLabel={t('common.close')}
    >
      <div className={styles.moreList}>
        {movie.tmdbId !== null && tmdb.enabled && (
          <Button block onClick={() => void onRefresh()} disabled={refreshing}>
            <Icon name="refresh" size={16} />
            {refreshing ? t('common.loading') : t('movie.refresh')}
          </Button>
        )}
        <Button variant="danger" block onClick={() => void onDelete()}>
          <Icon name="trash" size={16} />
          {t('movie.delete')}
        </Button>
      </div>
    </Sheet>
  );
}
